<template>
  <div class="erp-module-menu">
    <v-menu
      v-model="menu"
      open-on-hover
      :open-delay="120"
      :close-delay="250"
      location="bottom"
      offset="8"
      transition="fade-transition"
      content-class="erp-module-overlay"
    >
      <!-- ================================================= -->
      <!-- MENU ACTIVATOR -->
      <!-- ================================================= -->
      <template #activator="{ props }">
        <v-btn
          v-bind="props"
          variant="text"
          class="erp-module-button text-none"
          :style="{
            color:
              theme.appBarColor ||
              theme.text
          }"
        >
          <v-icon
            start
            size="20"
          >
            mdi-apps
          </v-icon>

          {{
            lang === 'en'
              ? 'ERP Module'
              : 'Module du système ERP'
          }}

          <v-icon
            end
            size="18"
            class="menu-chevron"
          >
            mdi-chevron-down
          </v-icon>
        </v-btn>
      </template>

      <!-- ================================================= -->
      <!-- ERP MODULE PANEL -->
      <!-- ================================================= -->
      <v-card
        elevation="10"
        class="erp-module-card"
        :style="{
          background:
            theme.cardBodyBg ||
            theme.surface,
          color:
            theme.cardBodyColor ||
            theme.text
        }"
      >
        <!-- =============================================== -->
        <!-- HEADER -->
        <!-- =============================================== -->
        <div class="module-header">
          <div class="module-header-icon">
            <v-icon
              size="28"
            >
              mdi-view-dashboard-outline
            </v-icon>
          </div>

          <div>
            <div class="module-header-title">
              {{
                lang === 'en'
                  ? 'Available Modules'
                  : 'Modules disponibles'
              }}
            </div>

            <div class="module-header-description">
              {{
                lang === 'en'
                  ? 'Select a business area to open its workspace.'
                  : 'Sélectionnez un domaine pour ouvrir son espace de travail.'
              }}
            </div>
          </div>
        </div>

        <v-divider />

        <!-- =============================================== -->
        <!-- MODULE GRID -->
        <!-- =============================================== -->
        <div class="module-grid">
          <router-link
            v-for="item in business"
            :key="item.key"
            :to="item.to"
            class="module-tile"
            @click="menu = false"
          >
            <!-- ICON -->
            <div
              class="module-icon"
              :style="{
                background:
                  theme.sectionBgColor ||
                  'rgba(127, 127, 127, 0.10)',

                color:
                  theme.appBarBg ||
                  theme.cardBodyColor ||
                  theme.text
              }"
            >
              <v-icon
                :icon="item.icon"
                size="38"
              />
            </div>

            <!-- CONTENT -->
            <div class="module-content">
              <div class="module-title">
                {{ item.title }}
              </div>

              <div class="module-description">
                {{ item.desc }}
              </div>
            </div>

            <!-- ARROW -->
            <div class="module-arrow">
              <v-icon size="19">
                mdi-arrow-right
              </v-icon>
            </div>
          </router-link>
        </div>

        <!-- =============================================== -->
        <!-- FOOTER -->
        <!-- =============================================== -->
        <div class="module-footer">
          <div class="module-footer-info">
            <v-icon
              size="20"
            >
              mdi-information-outline
            </v-icon>

            <span>
              {{
                lang === 'en'
                  ? 'Modules are displayed according to your access.'
                  : 'Les modules sont affichés selon vos accès.'
              }}
            </span>
          </div>
        </div>
      </v-card>
    </v-menu>
  </div>

  <v-spacer />
</template>

<script setup>
import {
  computed,
  ref,
  watch
} from 'vue'

import {
  useRouter
} from 'vue-router'

import {
  useI18n
} from 'vue-i18n'

import {
  useLangStore
} from '@/store/lang'

import {
  useThemeStore
} from '@/store/auth'

// ============================================================
// ROUTER / STORES
// ============================================================

const router =
  useRouter()

const langStore =
  useLangStore()

const themeStore =
  useThemeStore()

const {
  locale
} = useI18n()

// ============================================================
// MENU
// ============================================================

const menu =
  ref(false)

// ============================================================
// LANGUAGE
// ============================================================

const lang =
  computed(
    () =>
      langStore.currentLanguage ||
      'en'
  )

watch(
  lang,
  newLanguage => {
    locale.value =
      newLanguage
  },
  {
    immediate: true
  }
)

// ============================================================
// THEME
// ============================================================

const theme =
  computed(
    () =>
      themeStore.themeStyles ||
      {}
  )

// ============================================================
// ERP MODULES
// ============================================================

const business =
  computed(() => [
    // ========================================================
    // ADMINISTRATION
    // ========================================================
    {
      key:
        'administration',

      title:
        lang.value === 'en'
          ? 'Administration'
          : 'Administration',

      desc:
        lang.value === 'en'
          ? 'System and portal management'
          : 'Gestion du système et du portail',

      icon:
        'mdi-shield-account-outline',

      to:
        router.resolve({
          name:
            'AdminDashboard'
        }).href
    },

    // ========================================================
    // FINANCE
    // ========================================================
    {
      key:
        'finance',

      title:
        lang.value === 'en'
          ? 'Finance'
          : 'Finance',

      desc:
        lang.value === 'en'
          ? 'Accounting and financial management'
          : 'Gestion comptable et financière',

      icon:
        'mdi-finance',

      to:
        router.resolve({
          name:
            'FinanceDashboard'
        }).href
    },

    // ========================================================
    // INVENTORY & LOGISTICS
    // ========================================================
    {
      key:
        'inventory-logistics',

      title:
        lang.value === 'en'
          ? 'Inventory & Logistics'
          : 'Inventaire et logistique',

      desc:
        lang.value === 'en'
          ? 'Inventory, stock and logistics management'
          : 'Gestion des stocks et de la logistique',

      icon:
        'mdi-warehouse',

      to:
        router.resolve({
          name:
            'InventoryLogisticsDashboard'
        }).href
    },

    // ========================================================
    // PAYROLL
    // ========================================================
    {
      key:
        'payroll',

      title:
        lang.value === 'en'
          ? 'Payroll'
          : 'Paie',

      desc:
        lang.value === 'en'
          ? 'Employee and payroll management'
          : 'Gestion des employés et de la paie',

      icon:
        'mdi-account-cash-outline',

      to:
        router.resolve({
          name:
            'PayrollDashboard'
        }).href
    },

    // ========================================================
    // PROCUREMENT
    // ========================================================
    {
      key:
        'procurement',

      title:
        lang.value === 'en'
          ? 'Procurement'
          : 'Approvisionnement',

      desc:
        lang.value === 'en'
          ? 'Purchasing and supplier management'
          : 'Gestion des achats et fournisseurs',

      icon:
        'mdi-cart-arrow-down',

      to:
        router.resolve({
          name:
            'ProcurementDashboard'
        }).href
    },

    // ========================================================
    // SALES
    // ========================================================
    {
      key:
        'sales',

      title:
        lang.value === 'en'
          ? 'Sales'
          : 'Ventes',

      desc:
        lang.value === 'en'
          ? 'Sales, customers and order management'
          : 'Gestion des ventes, clients et commandes',

      icon:
        'mdi-cart-outline',

      to:
        router.resolve({
          name:
            'SalesDashboard'
        }).href
    },

    // ========================================================
    // MEDICAL / CLINICAL
    // ========================================================
    {
      key:
        'clinical',

      title:
        lang.value === 'en'
          ? 'Medical / Clinical'
          : 'Médical / Clinique',

      desc:
        lang.value === 'en'
          ? 'Clinical and patient care management'
          : 'Gestion clinique et soins aux patients',

      icon:
        'mdi-hospital-box-outline',

      to:
        router.resolve({
          name:
            'ClinicalDashboard'
        }).href
    }
  ])
</script>

<style scoped>
/* ============================================================
   ROOT
============================================================ */

.erp-module-menu {
  display: flex;
  align-items: center;
}

/* ============================================================
   ACTIVATOR
============================================================ */

.erp-module-button {
  min-height: 44px;

  margin-top: 20px;

  padding-right: 10px;
  padding-left: 10px;

  font-size: 16px;
  font-weight: 400;
}

.menu-chevron {
  transition:
    transform 0.2s ease;
}

/* ============================================================
   CARD
============================================================ */

.erp-module-card {
  width:
    min(
      940px,
      94vw
    );

  padding: 0;

  overflow: hidden;

  border-radius: 14px;
}

/* ============================================================
   HEADER
============================================================ */

.module-header {
  display: flex;

  align-items: center;

  gap: 15px;

  padding:
    24px 28px;
}

.module-header-icon {
  display: flex;

  width: 52px;
  height: 52px;

  flex:
    0 0 52px;

  align-items: center;
  justify-content: center;

  border-radius: 12px;

  background:
    rgba(
      127,
      127,
      127,
      0.1
    );
}

.module-header-title {
  font-size: 21px;
  font-weight: 700;
}

.module-header-description {
  margin-top: 3px;

  font-size: 12px;

  opacity: 0.65;
}

/* ============================================================
   GRID
============================================================ */

.module-grid {
  display: grid;

  grid-template-columns:
    repeat(
      4,
      minmax(0, 1fr)
    );

  gap: 1px;

  padding: 20px;

  background:
    rgba(
      127,
      127,
      127,
      0.08
    );
}

/* ============================================================
   MODULE TILE
============================================================ */

.module-tile {
  position: relative;

  display: flex;

  min-height: 170px;

  flex-direction: column;

  align-items: center;
  justify-content: center;

  padding:
    22px 16px;

  background:
    rgb(
      var(
        --v-theme-surface
      )
    );

  color: inherit;

  text-align: center;

  text-decoration: none;

  transition:
    transform 0.18s ease,
    box-shadow 0.18s ease,
    background-color 0.18s ease;

  overflow: hidden;
}

.module-tile:hover {
  z-index: 2;

  transform:
    translateY(-3px);

  box-shadow:
    0 10px 28px
    rgba(
      0,
      0,
      0,
      0.12
    );
}

/* ============================================================
   MODULE ICON
============================================================ */

.module-icon {
  display: flex;

  width: 72px;
  height: 72px;

  align-items: center;
  justify-content: center;

  margin-bottom: 14px;

  border-radius: 16px;

  transition:
    transform 0.18s ease;
}

.module-tile:hover
.module-icon {
  transform:
    scale(1.06);
}

/* ============================================================
   MODULE CONTENT
============================================================ */

.module-content {
  width: 100%;
}

.module-title {
  margin-bottom: 5px;

  font-size: 14px;
  font-weight: 700;

  line-height: 1.3;
}

.module-description {
  max-width: 180px;

  margin:
    0 auto;

  font-size: 10.5px;

  line-height: 1.5;

  opacity: 0.63;
}

/* ============================================================
   ARROW
============================================================ */

.module-arrow {
  position: absolute;

  right: 12px;
  bottom: 10px;

  display: flex;

  width: 28px;
  height: 28px;

  align-items: center;
  justify-content: center;

  border-radius: 50%;

  opacity: 0;

  transform:
    translateX(-5px);

  transition:
    opacity 0.18s ease,
    transform 0.18s ease;

  background:
    rgba(
      127,
      127,
      127,
      0.1
    );
}

.module-tile:hover
.module-arrow {
  opacity: 1;

  transform:
    translateX(0);
}

/* ============================================================
   FOOTER
============================================================ */

.module-footer {
  padding:
    13px 26px;

  border-top:
    1px solid
    rgba(
      127,
      127,
      127,
      0.15
    );
}

.module-footer-info {
  display: flex;

  align-items: center;

  gap: 8px;

  font-size: 10.5px;

  opacity: 0.65;
}

/* ============================================================
   TABLET
============================================================ */

@media (
  max-width: 959px
) {
  .erp-module-card {
    width:
      min(
        650px,
        94vw
      );

    max-height:
      75vh;

    overflow-y: auto;
  }

  .module-grid {
    grid-template-columns:
      repeat(
        2,
        minmax(0, 1fr)
      );
  }
}

/* ============================================================
   MOBILE
============================================================ */

@media (
  max-width: 599px
) {
  .module-header {
    padding:
      18px;
  }

  .module-header-title {
    font-size:
      18px;
  }

  .module-grid {
    grid-template-columns:
      1fr;

    padding:
      10px;
  }

  .module-tile {
    min-height:
      125px;

    flex-direction:
      row;

    justify-content:
      flex-start;

    gap:
      15px;

    padding:
      16px;

    text-align:
      left;
  }

  .module-icon {
    width:
      55px;

    height:
      55px;

    flex:
      0 0 55px;

    margin-bottom:
      0;

    border-radius:
      12px;
  }

  .module-description {
    max-width:
      none;

    margin:
      0;
  }

  .module-arrow {
    top:
      50%;

    right:
      12px;

    bottom:
      auto;

    opacity:
      0.6;

    transform:
      translateY(-50%);
  }
}
</style>